import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { RiCloseLine, RiLoader4Line } from 'react-icons/ri'
import toast from 'react-hot-toast'
import { CanvaLogo } from '@/components/icons/CanvaLogo'
import { useCanvaListDesigns, useCanvaImportDesign, useCanvaRefreshToken } from '@/api/useCanva'
import type { CanvaDesign } from '@/api/useCanva'
import {
    getCanvaAccessToken,
    getCanvaRefreshToken,
    storeCanvaTokens,
    startCanvaAuth,
    isCanvaConnected,
} from '@/lib/canvaOAuth'
import { buildRoute } from '@/constants/routes'
import styles from './CanvaImportModal.module.css'

interface CanvaImportModalProps {
    visible: boolean
    onClose: () => void
}

// Default doc size if we can't determine from thumbnail (A4 in mm)
const DEFAULT_WIDTH_MM = 210
const DEFAULT_HEIGHT_MM = 297

export function CanvaImportModal({ visible, onClose }: CanvaImportModalProps) {
    const navigate = useNavigate()
    const [designs, setDesigns] = useState<CanvaDesign[]>([])
    const [loading, setLoading] = useState(true)
    const [hasLoaded, setHasLoaded] = useState(false)
    const [importing, setImporting] = useState<CanvaDesign | null>(null)
    const [importStep, setImportStep] = useState('')
    const [accessToken, setAccessToken] = useState<string | null>(null)

    const listDesigns = useCanvaListDesigns()
    const importDesign = useCanvaImportDesign()
    const refreshTokenMut = useCanvaRefreshToken()

    const ensureAccessToken = useCallback(async (): Promise<string | null> => {
        let token = getCanvaAccessToken()
        if (token) {
            setAccessToken(token)
            return token
        }

        const refresh = getCanvaRefreshToken()
        if (!refresh) return null

        try {
            const result = await refreshTokenMut.mutateAsync(refresh)
            storeCanvaTokens(result.access_token, result.refresh_token, result.expires_in)
            setAccessToken(result.access_token)
            return result.access_token
        } catch {
            return null
        }
    }, [refreshTokenMut])

    useEffect(() => {
        if (!visible) {
            setHasLoaded(false)
            setLoading(true)
            return
        }

        const loadDesigns = async () => {
            const token = await ensureAccessToken()
            if (!token) {
                setLoading(false)
                setHasLoaded(true)
                return
            }

            setLoading(true)
            try {
                const result = await listDesigns.mutateAsync({ canva_access_token: token })
                setDesigns(result.items || [])
            } catch {
                toast.error('Error al cargar diseños de Canva')
            } finally {
                setLoading(false)
                setHasLoaded(true)
            }
        }

        loadDesigns()
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible])

    const handleImport = useCallback(async (design: CanvaDesign) => {
        if (!accessToken) return

        setImporting(design)
        setImportStep('Exportando diseño de Canva...')

        try {
            // Determine dimensions from thumbnail aspect ratio
            let widthMm = DEFAULT_WIDTH_MM
            let heightMm = DEFAULT_HEIGHT_MM
            if (design.thumbnail?.width && design.thumbnail?.height) {
                const aspect = design.thumbnail.width / design.thumbnail.height
                if (aspect > 1) {
                    widthMm = 297
                    heightMm = Math.round(297 / aspect)
                } else if (Math.abs(aspect - 1) < 0.1) {
                    widthMm = 210
                    heightMm = 210
                } else {
                    widthMm = Math.round(210 * aspect)
                    heightMm = 297
                }
            }

            const title = design.title || 'Diseño de Canva'
            setImportStep('Descargando páginas...')

            // Single API call: backend handles export → download → GCS → project creation
            const project = await importDesign.mutateAsync({
                canva_access_token: accessToken,
                design_id: design.id,
                title,
                width_mm: widthMm,
                height_mm: heightMm,
                format: 'png',
            })

            toast.success(`"${title}" importado (${project.pages.length} página${project.pages.length !== 1 ? 's' : ''})`)
            onClose()
            navigate(buildRoute.editor(project.id))
        } catch (err) {
            console.error('Canva import failed:', err)
            toast.error('Error al importar desde Canva')
        } finally {
            setImporting(null)
            setImportStep('')
        }
    }, [accessToken, importDesign, onClose, navigate])

    const handleConnect = useCallback(() => {
        startCanvaAuth()
    }, [])

    if (!visible) return null

    const connected = isCanvaConnected()

    // Show importing overlay
    if (importing) {
        return (
            <div className={styles.overlay}>
                <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                    <div className={styles.importingState}>
                        {importing.thumbnail?.url && (
                            <img
                                src={importing.thumbnail.url}
                                alt={importing.title}
                                className={styles.importingThumb}
                            />
                        )}
                        <RiLoader4Line className={styles.spinner} />
                        <p className={styles.importingTitle}>{importing.title || 'Diseño de Canva'}</p>
                        <p className={styles.importingStep}>{importStep}</p>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className={styles.overlay} onClick={onClose}>
            <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                <div className={styles.header}>
                    <div className={styles.headerLeft}>
                        <CanvaLogo size={24} />
                        <h2 className={styles.title}>Importar desde Canva</h2>
                    </div>
                    <button className={styles.closeBtn} onClick={onClose}>
                        <RiCloseLine />
                    </button>
                </div>

                {!connected ? (
                    <div className={styles.connectState}>
                        <p className={styles.connectText}>
                            Conecta tu cuenta de Canva para importar tus diseños directamente.
                        </p>
                        <button className={styles.connectBtn} onClick={handleConnect}>
                            Conectar con Canva
                        </button>
                    </div>
                ) : loading || !hasLoaded ? (
                    <div className={styles.loadingState}>
                        <RiLoader4Line className={styles.spinner} />
                        <p>Cargando tus diseños...</p>
                    </div>
                ) : designs.length === 0 ? (
                    <div className={styles.emptyState}>
                        <p>No se encontraron diseños en tu cuenta de Canva.</p>
                    </div>
                ) : (
                    <div className={styles.designGrid}>
                        {designs.map((design) => (
                            <div
                                key={design.id}
                                className={styles.designCard}
                                onClick={() => handleImport(design)}
                            >
                                <div className={styles.designThumb}>
                                    {design.thumbnail?.url ? (
                                        <img src={design.thumbnail.url} alt={design.title} />
                                    ) : (
                                        <div className={styles.noThumb}>Sin vista previa</div>
                                    )}
                                </div>
                                <span className={styles.designTitle}>{design.title || 'Sin título'}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    )
}
